import { useSnackbar } from "notistack";
import { useEffect, useRef, useState } from "react";

import { IdName, MasterAgentSearchTag } from "@/api/generated";
import { PropallyApiFactory } from "@/module/CustomApiFactory";

let masterAgentSearchTagsCache: MasterAgentSearchTag[] | null = null;
let masterAgentSearchTagsInFlight: Promise<MasterAgentSearchTag[]> | null = null;

// 画面に出せるタグの条件。キャッシュの保存判定と表示用の絞り込みで基準がずれると、
// 「生レスポンスは非空だが表示できるタグは0件」のときにキャッシュへ載ってしまい、
// 再試行ボタンが二度と API を叩かない無反応ボタンになるため、両者でこれを使う
type DisplayableMasterAgentSearchTag = Required<Pick<MasterAgentSearchTag, "id" | "name">> & MasterAgentSearchTag;

const isDisplayableMasterAgentSearchTag = (t: MasterAgentSearchTag): t is DisplayableMasterAgentSearchTag =>
  typeof t.id === "number" && typeof t.name === "string" && t.name.length > 0;

const getMasterAgentSearchTagsCached = async (fp: ReturnType<typeof PropallyApiFactory>) => {
  // 「取得済みか」は null かどうかで判定する（truthy 判定にしない）。空配列も truthy なので、
  // サーバが 200 で [] を返すとキャッシュが 0 件のまま固着し、再試行ボタンが二度と API を叩かない
  // 無反応ボタンになってしまう。0 件は成功として保持せず（下の代入で除外）、再取得を許す
  if (masterAgentSearchTagsCache !== null) return masterAgentSearchTagsCache;
  if (masterAgentSearchTagsInFlight) return masterAgentSearchTagsInFlight;

  masterAgentSearchTagsInFlight = fp
    .v1MasterDataAgentSearchTagsGet({
      withCredentials: true,
    })
    .then((response) => {
      const sorted = [...response.data].sort((a, b) => {
        const aOrder = a.order ?? 9999;
        const bOrder = b.order ?? 9999;
        if (aOrder !== bOrder) return aOrder - bOrder;
        return (a.id ?? 0) - (b.id ?? 0);
      });
      // 表示できるタグが 1 件も無いときはキャッシュしない。マスタが空（または id/name 欠落だけ）で
      // 返るのは異常系（サーバ側の握り潰し等）であり、ここで保持すると再試行しても同じ結果を返し続ける。
      // 判定は表示側と同じ isDisplayableMasterAgentSearchTag を使い、基準のズレを作らない。
      // トレードオフとして、マスタが 0 件を返し続ける間は、このキャッシュを共有する全画面
      // （プランナー検索・プランナー詳細・オファー系3画面）のマウントごとに GET が飛ぶ。
      // 継続的に失敗する状況（401 継続など）では画面遷移のたびに snackbar も出る。
      // 無限リトライにはならず、タグが 1 件でも登録されれば解消するため許容する
      if (sorted.some(isDisplayableMasterAgentSearchTag)) masterAgentSearchTagsCache = sorted;
      return sorted;
    })
    .finally(() => {
      masterAgentSearchTagsInFlight = null;
    });

  return masterAgentSearchTagsInFlight;
};

export const useGetMasterResidenceTypes = (): {
  masterResidenceTypes: IdName[];
  loading: boolean;
} => {
  const { enqueueSnackbar } = useSnackbar();

  // 初回レンダーから true にする。false 始まりだと setLoading(true) が useEffect の中なので
  // 「取得前かつ loading=false」の1レンダーが生まれ、呼び出し元のローディング判定をすり抜けて
  // 空の masterResidenceTypes で子を描画してしまう（Sentry PROPALLY_WEB-26D / 26S の原因）。
  const [loading, setLoading] = useState(true);
  const [masterResidenceTypes, setMasterResidenceTypes] = useState([]);

  const fp = PropallyApiFactory();

  useEffect(() => {
    // アンマウント後の setState（React の unmounted component 警告）を防ぐガード
    let cancelled = false;

    const getMasterResidenceTypes = async () => {
      try {
        const response = await fp.v1MasterDataResidenceTypesGet({
          withCredentials: true,
        });

        if (cancelled) return;
        setMasterResidenceTypes(response.data);
      } catch (e) {
        if (cancelled) return;
        if (e.response?.status === 401) {
          enqueueSnackbar("画面をリロードし、再度ログインしてください");
        } else {
          enqueueSnackbar("ネットワークエラーです。しばらくたってからお試しください");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    getMasterResidenceTypes();

    return () => {
      cancelled = true;
    };
  }, []);

  return { masterResidenceTypes, loading };
};

export const useGetMasterTags = (): {
  masterTags: IdName[];
  loading: boolean;
} => {
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(false);
  const [masterTags, setMasterTags] = useState([]);

  const fp = PropallyApiFactory();

  const getMasterTags = async () => {
    try {
      setLoading(true);
      const response = await fp.v1MasterDataTagsGet({
        withCredentials: true,
      });

      setMasterTags(response.data);
    } catch (e) {
      if (e.response?.status === 401) {
        enqueueSnackbar("画面をリロードし、再度ログインしてください");
      } else {
        enqueueSnackbar("ネットワークエラーです。しばらくたってからお試しください");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getMasterTags();
  }, []);

  return { masterTags, loading };
};

export const useGetMasterInterestedPropertyTypes = (): {
  masterInterestedPropertyTypes: IdName[];
  loading: boolean;
} => {
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(false);
  const [masterInterestedPropertyTypes, setMasterInterestedPropertyTypes] = useState([]);

  const fp = PropallyApiFactory();

  useEffect(() => {
    // アンマウント後の setState（React の unmounted component 警告）を防ぐガード
    let cancelled = false;

    const getMasterInterestedPropertyTypes = async () => {
      try {
        setLoading(true);
        const response = await fp.v1MasterDataInterestedPropertyTypesGet({
          withCredentials: true,
        });

        if (cancelled) return;
        setMasterInterestedPropertyTypes(response.data);
      } catch (e) {
        if (cancelled) return;
        if (e.response?.status === 401) {
          enqueueSnackbar("画面をリロードし、再度ログインしてください");
        } else {
          enqueueSnackbar("ネットワークエラーです。しばらくたってからお試しください");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    getMasterInterestedPropertyTypes();

    return () => {
      cancelled = true;
    };
  }, []);

  return { masterInterestedPropertyTypes, loading };
};

export const useGetMasterPurposeOfUses = (): {
  masterPurposeOfUses: IdName[];
  loading: boolean;
} => {
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(false);
  const [masterPurposeOfUses, setMasterPurposeOfUses] = useState([]);

  const fp = PropallyApiFactory();

  const getPurposeOfUses = async () => {
    try {
      setLoading(true);
      const response = await fp.v1MasterDataPurposeOfUsesGet({
        withCredentials: true,
      });

      setMasterPurposeOfUses(response.data);
    } catch (e) {
      if (e.response?.status === 401) {
        enqueueSnackbar("画面をリロードし、再度ログインしてください");
      } else {
        enqueueSnackbar("ネットワークエラーです。しばらくたってからお試しください");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getPurposeOfUses();
  }, []);

  return { masterPurposeOfUses, loading };
};

export const useGetMasterInflowRoutes = (): {
  masterInflowRoutes: IdName[];
  loading: boolean;
} => {
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(false);
  const [masterInflowRoutes, setMasterInflowRoutes] = useState([]);

  const fp = PropallyApiFactory();

  const getInflowRoutes = async () => {
    try {
      setLoading(true);
      const response = await fp.v1MasterDataInflowRoutesGet({
        withCredentials: true,
      });

      setMasterInflowRoutes(response.data);
    } catch (e) {
      if (e.response?.status === 401) {
        enqueueSnackbar("画面をリロードし、再度ログインしてください");
      } else {
        enqueueSnackbar("ネットワークエラーです。しばらくたってからお試しください");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getInflowRoutes();
  }, []);

  return { masterInflowRoutes, loading };
};

export const useGetMasterAgentSearchTags = (): {
  masterAgentSearchTags: MasterAgentSearchTag[];
  loading: boolean;
  refetch: () => Promise<void>;
} => {
  const { enqueueSnackbar } = useSnackbar();

  // 取得はマウント時の effect（初回ペイント後）で始まる。初期値を false にすると
  // 取得前の1フレームが「取得完了して0件」と区別できず、呼び出し側が
  // 「条件を読み込めませんでした」を誤表示するため true から始める。
  const [loading, setLoading] = useState(true);
  const [masterAgentSearchTags, setMasterAgentSearchTags] = useState<MasterAgentSearchTag[]>([]);
  // 再試行ボタンの連打で取得が重ならないようにするガード。loading の state だと同一レンダー内の
  // 連続呼び出しに間に合わないため ref で見る
  const fetchingRef = useRef(false);

  const fp = PropallyApiFactory();

  const getMasterAgentSearchTags = async () => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    try {
      setLoading(true);
      // 取得済みならモジュールキャッシュがそのまま返るため、再試行しても成功済みの結果は捨てない
      const tags = await getMasterAgentSearchTagsCached(fp);
      setMasterAgentSearchTags(tags);
    } catch (e) {
      if (e.response?.status === 401) {
        enqueueSnackbar("画面をリロードし、再度ログインしてください");
      } else {
        enqueueSnackbar("ネットワークエラーです。しばらくたってからお試しください");
      }
    } finally {
      fetchingRef.current = false;
      setLoading(false);
    }
  };

  useEffect(() => {
    getMasterAgentSearchTags();
  }, []);

  // 取得に失敗するとタグが無いまま検索条件を選べなくなるため、呼び出し側に再取得の手段を渡す
  return { masterAgentSearchTags, loading, refetch: getMasterAgentSearchTags };
};

/**
 * エージェント検索タグの「id→name」辞書を返す（APIマスター駆動）。
 */
export const useMasterAgentSearchTagMap = (): {
  tagIdToNameMap: Record<number, string>;
  tags: Array<{ id: number; name: string; order?: number | null }>;
  loading: boolean;
  refetch: () => Promise<void>;
} => {
  const { masterAgentSearchTags, loading, refetch } = useGetMasterAgentSearchTags();

  if (masterAgentSearchTags.length > 0) {
    const tags = masterAgentSearchTags
      .filter(isDisplayableMasterAgentSearchTag)
      .map((t) => ({ id: t.id, name: t.name, order: t.order }));
    const tagIdToNameMap = Object.fromEntries(tags.map((t) => [t.id, t.name]));
    return { tagIdToNameMap, tags, loading, refetch };
  }

  return { tagIdToNameMap: {}, tags: [], loading, refetch };
};
